import Util from '../forecast-app/shared/util/util';
import * as tracking from '../tracking';
import {graphql} from 'react-relay';
import {omit} from 'lodash';
import type {Environment} from 'react-relay';
import {Disposable, UploadableMap} from 'relay-runtime';
import {stageTrackingStatus} from '../forecast-app/shared/util/projectStagePicker';
import {trackAndCommitMutation} from '../tracking/amplitude/TrackMutation';
import {RelayOnCompleted, RelayOnError} from './MutationTypes';
import {
	UpdateProjectInput,
	updateProjectBudgetPageMutationModernMutation,
} from './__generated__/updateProjectBudgetPageMutationModernMutation.graphql';

/** The mutation input, with `project`, the project being changed, standing in for `id`. */
type UpdateProjectBudgetPageMutationInput = Omit<UpdateProjectInput, 'csrfToken' | 'socketClientId' | 'clientMutationId'> & {
	project: {id: string};
};

const mutation = graphql`
	mutation updateProjectBudgetPageMutationModernMutation($input: UpdateProjectInput!) {
		updateProject(input: $input) {
			errors
			project {
				id
				estimationUnit # warnings
				useBaseline
				projectStartYear # warnings
				projectStartMonth # warnings
				projectStartDay # warnings
				projectEndYear
				projectEndMonth
				projectEndDay
				companyProjectId
				warnings {
					id
					year
					month
					day
					tasks(first: 10000) {
						edges {
							node {
								id
								name
								startDay
								startMonth
								startYear
								deadlineDay
								deadlineMonth
								deadlineYear
								estimateForecast
							}
						}
					}
					personIds
					expenseItems(first: 10000) {
						edges {
							node {
								id
								name
								expenseYear
								expenseMonth
								expenseDay
							}
						}
					}
					timeRegistrations(first: 10000) {
						edges {
							node {
								id
								year
								month
								day
								minutesRegistered
								person {
									id
									fullName
									profilePictureId
									profilePictureDefaultId
								}
								task {
									name
								}
							}
						}
					}
				}
				...BudgetPage_project
				...expensesPage_project
				...ratesAndInternalCostPage_project
			}
			program {
				id
				name
				prefix
				budgetType
				projects(first: 10000) @connection(key: "Program_projects", filters: []) {
					edges {
						node {
							id
							serviceCompanyId
							companyProjectId
							name
							prefix
							budgetType
							budget
							defaultPeriodBudgetType
							financialNumbers {
								allActualTimeAndExpenses
								recognitionProfitPercentage
								allTotalTimeAndExpensesAtCompletion
								forecastCostToComplete
							}
							financialNumbersToDate {
								recognitionActualProfitPercentage
								allActualTimeAndExpenses
							}
							totalRevenueRecognition
							totalRevenueRecognitionToDate
							totalTimeRevenueRecognitionToDate
							totalBillableExpensesAtCompletion
						}
					}
				}
				projectsFinancialsTotals {
					recognitionForecastRevenue # Revenue - Recognition at completion
					allActualTimeAndExpenses # Spend - Actual to date
					allTotalTimeAndExpensesAtCompletion # Spend - Total at completion
					recognitionProfitPercentage # Profit margin - total at completion
					recognitionActualProfitPercentage # Profit margin - total at completion
				}
				projectsFinancialsToDate {
					totalActualRevenueRecognition # Revenue - recognised to date
					recognitionAmount # Revenue - recognised to date
					recognitionActualProfitPercentage # Profit margin - actual to date
					allActualTimeAndExpenses # Spend - actual to date
				}
			}
		}
	}
`;

function commit(
	environment: Environment,
	input: UpdateProjectBudgetPageMutationInput,
	onSuccess?: RelayOnCompleted<updateProjectBudgetPageMutationModernMutation>,
	onError?: RelayOnError,
	uploadables?: UploadableMap
): Disposable {
	Util.localStorageDeleteFincancialMap();
	const changes: string[] = [];

	let status: string | null | undefined;
	let stageId: string | null | undefined;

	for (const [key, value] of Object.entries(input)) {
		if (['id', 'project'].includes(key) || value === undefined) {
			continue;
		}

		if (key === 'status') {
			status = input.status;
		}
		if (key === 'stageId') {
			stageId = input.stageId;
		}

		changes.push(key);
	}

	const options: {_Changed: string[]; Status?: string; StageId?: string} = {_Changed: changes};
	if (status) {
		options.Status = status;
	}
	if (stageId) {
		options.StageId = stageId;
		options.Status = stageTrackingStatus(stageId) ?? options.Status;
	}
	tracking.trackEvent('Project Updated', options);

	const cleanedVariables: Omit<UpdateProjectBudgetPageMutationInput, 'project'> = omit(input, ['project']);
	cleanedVariables.id = input.project.id;

	const variables = {input: {csrfToken: Util.getCsrfValue(), socketClientId: Util.getClientId(), ...cleanedVariables}};
	return trackAndCommitMutation<updateProjectBudgetPageMutationModernMutation>(environment, {
		mutation,
		variables,
		uploadables,
		onCompleted: onSuccess,
		onError: onError,
	});
}

export default {commit};
